import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { Building2, UserPlus, Check, X, Copy, ExternalLink, Loader2, Filter, RefreshCw } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const authHeader = () => {
  const t = localStorage.getItem('savenes_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

export default function AccessRequestsAdminPage() {
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [typeFilter, setTypeFilter] = useState('');
  const [approveTarget, setApproveTarget] = useState(null);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [approvalResult, setApprovalResult] = useState(null);
  const [copiedField, setCopiedField] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.set('status', statusFilter);
      if (typeFilter) params.set('type', typeFilter);
      const r = await axios.get(`${API_URL}/api/access-requests?${params}`, { headers: authHeader() });
      setRequests(r.data.requests || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not load requests');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [statusFilter, typeFilter]);

  const handleApprove = async (req) => {
    setBusy(true);
    try {
      const r = await axios.post(`${API_URL}/api/access-requests/${req.id}/approve`, {}, { headers: authHeader() });
      setApprovalResult(r.data);
      setApproveTarget(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Approval failed');
    } finally { setBusy(false); }
  };

  const handleReject = async () => {
    if (!rejectTarget) return;
    setBusy(true);
    try {
      await axios.post(`${API_URL}/api/access-requests/${rejectTarget.id}/reject`, { reason: rejectReason }, { headers: authHeader() });
      toast.success('Request rejected');
      setRejectTarget(null);
      setRejectReason('');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Rejection failed');
    } finally { setBusy(false); }
  };

  const copy = (key, value) => {
    navigator.clipboard.writeText(value);
    setCopiedField(key);
    setTimeout(() => setCopiedField(''), 1500);
  };

  return (
    <div className="p-6 space-y-6" data-testid="access-requests-admin">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#0A2342]">Access Requests</h1>
          <p className="text-sm text-[#64748B] mt-1">Review new-organization signups and employee join requests.</p>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="refresh-requests">
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-3 p-4 bg-white rounded-xl border border-[#E2E8F0]">
        <Filter className="w-4 h-4 text-[#64748B]" />
        <div className="flex items-center gap-2">
          <Label className="text-xs text-[#64748B]">Status</Label>
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="text-sm rounded-md border border-[#E2E8F0] px-2 py-1 bg-white" data-testid="filter-status">
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
            <option value="">All</option>
          </select>
        </div>
        <div className="flex items-center gap-2">
          <Label className="text-xs text-[#64748B]">Type</Label>
          <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)} className="text-sm rounded-md border border-[#E2E8F0] px-2 py-1 bg-white" data-testid="filter-type">
            <option value="">All</option>
            <option value="organization">New organization</option>
            <option value="employee">Employee join</option>
          </select>
        </div>
        <div className="text-xs text-[#64748B] ml-auto">{requests.length} {requests.length === 1 ? 'request' : 'requests'}</div>
      </div>

      {loading && <div className="text-center py-12 text-[#64748B]"><Loader2 className="w-6 h-6 animate-spin inline mr-2" /> Loading…</div>}

      {!loading && requests.length === 0 && (
        <div className="text-center py-16 text-[#64748B] bg-white rounded-xl border border-[#E2E8F0]">
          No requests match the filter.
        </div>
      )}

      <div className="space-y-3">
        {requests.map(r => (
          <RequestCard key={r.id} req={r} onApprove={() => setApproveTarget(r)} onReject={() => setRejectTarget(r)} />
        ))}
      </div>

      {/* Approval confirmation */}
      <Dialog open={!!approveTarget} onOpenChange={open => { if (!open) setApproveTarget(null); }}>
        <DialogContent data-testid="approve-dialog">
          <DialogHeader>
            <DialogTitle>Approve request?</DialogTitle>
          </DialogHeader>
          {approveTarget && (
            <div className="space-y-3 text-sm">
              {approveTarget.type === 'organization' ? (
                <div className="p-3 bg-[#F0FDFA] rounded-md border border-[#0E7490]/20">
                  <div className="text-xs text-[#64748B]">This will create:</div>
                  <ul className="list-disc ml-5 mt-1 space-y-1">
                    <li>Organization <strong>{approveTarget.organization_name}</strong> on a 14-day Starter trial</li>
                    <li>Practice-admin user <strong>{approveTarget.contact_email}</strong></li>
                    <li>A welcome email queued with a temporary password (also shown to you)</li>
                  </ul>
                </div>
              ) : (
                <div className="p-3 bg-[#F0FDFA] rounded-md border border-[#0E7490]/20">
                  <div className="text-xs text-[#64748B]">This will create:</div>
                  <ul className="list-disc ml-5 mt-1 space-y-1">
                    <li>User <strong>{approveTarget.full_name}</strong> ({approveTarget.email})</li>
                    <li>Role: <strong>{approveTarget.requested_role}</strong> in their organization</li>
                    <li>A welcome email queued with a temporary password (also shown to you)</li>
                  </ul>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setApproveTarget(null)} disabled={busy}>Cancel</Button>
            <Button onClick={() => handleApprove(approveTarget)} disabled={busy} className="bg-[#0E7490] hover:bg-[#0E7490]/90 text-white" data-testid="confirm-approve">
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
              Approve & create user
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rejection dialog */}
      <Dialog open={!!rejectTarget} onOpenChange={open => { if (!open) { setRejectTarget(null); setRejectReason(''); } }}>
        <DialogContent data-testid="reject-dialog">
          <DialogHeader>
            <DialogTitle>Reject request</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <Label htmlFor="reject-reason">Reason (optional, shown to requester if you choose to share)</Label>
            <textarea id="reject-reason" rows={3} value={rejectReason} onChange={e => setRejectReason(e.target.value)}
                      className="w-full rounded-md border border-[#E2E8F0] px-3 py-2 text-sm" data-testid="reject-reason"
                      placeholder="e.g. We couldn't verify this organization is a healthcare provider…" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setRejectTarget(null); setRejectReason(''); }} disabled={busy}>Cancel</Button>
            <Button onClick={handleReject} disabled={busy} variant="destructive" data-testid="confirm-reject">
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <X className="w-4 h-4 mr-2" />}
              Reject
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Approval result — show credentials */}
      <Dialog open={!!approvalResult} onOpenChange={open => { if (!open) setApprovalResult(null); }}>
        <DialogContent className="sm:max-w-md" data-testid="approval-result-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Check className="w-5 h-5 text-[#10B981]" /> Account created
            </DialogTitle>
          </DialogHeader>
          {approvalResult && (
            <div className="space-y-3 text-sm">
              <p className="text-[#64748B]">
                A welcome email has been queued. As a fallback, here are the credentials — keep them safe.
              </p>
              <div className="space-y-2">
                <CredRow label="Email" value={approvalResult.credentials?.email} field="email" onCopy={copy} copied={copiedField === 'email'} />
                <CredRow label="Temporary password" value={approvalResult.credentials?.temp_password} field="pw" mono onCopy={copy} copied={copiedField === 'pw'} />
              </div>
              <p className="text-xs text-[#94A3B8]">The user will be prompted to change this password on first sign-in.</p>
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setApprovalResult(null)} className="bg-[#0E7490] hover:bg-[#0E7490]/90 text-white" data-testid="close-approval-result">Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function RequestCard({ req, onApprove, onReject }) {
  const isOrg = req.type === 'organization';
  const Icon = isOrg ? Building2 : UserPlus;
  const status = req.status;
  const statusColor = {
    pending: 'bg-amber-100 text-amber-700 border-amber-200',
    approved: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    rejected: 'bg-rose-100 text-rose-700 border-rose-200',
  }[status] || 'bg-slate-100 text-slate-700';
  return (
    <Card className="p-4 border-[#E2E8F0]" data-testid={`request-card-${req.id}`}>
      <div className="flex items-start gap-4">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${isOrg ? 'bg-[#0E7490]/10' : 'bg-[#F59E0B]/10'}`}>
          <Icon className={`w-5 h-5 ${isOrg ? 'text-[#0E7490]' : 'text-[#F59E0B]'}`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <h3 className="font-semibold text-[#0A2342] truncate">
                {isOrg ? req.organization_name : req.full_name}
              </h3>
              <p className="text-xs text-[#64748B] mt-0.5 truncate">
                {isOrg
                  ? `${req.contact_name} · ${req.contact_email}${req.contact_phone ? ' · ' + req.contact_phone : ''}`
                  : `${req.email} · wants ${req.requested_role}${req.phone ? ' · ' + req.phone : ''}`}
              </p>
            </div>
            <Badge className={`text-xs px-2 py-0.5 border ${statusColor}`}>{status}</Badge>
          </div>
          {isOrg && (req.organization_size || req.role_at_org) && (
            <p className="text-xs text-[#64748B] mt-2">
              {req.role_at_org && <span>Role: {req.role_at_org}</span>}
              {req.role_at_org && req.organization_size && <span className="mx-2">·</span>}
              {req.organization_size && <span>Team: {req.organization_size}</span>}
            </p>
          )}
          {req.message && <p className="text-xs text-[#64748B] mt-2 italic">"{req.message}"</p>}
          <p className="text-xs text-[#94A3B8] mt-2">Submitted {new Date(req.created_at).toLocaleString()}</p>
          {req.rejection_reason && (
            <p className="text-xs text-rose-700 mt-1 italic">Rejection note: {req.rejection_reason}</p>
          )}
          {status === 'pending' && (
            <div className="flex items-center gap-2 mt-3">
              <Button size="sm" onClick={onApprove} className="bg-[#0E7490] hover:bg-[#0E7490]/90 text-white" data-testid={`approve-btn-${req.id}`}>
                <Check className="w-4 h-4 mr-1" /> Approve
              </Button>
              <Button size="sm" variant="outline" onClick={onReject} className="border-rose-200 text-rose-700 hover:bg-rose-50" data-testid={`reject-btn-${req.id}`}>
                <X className="w-4 h-4 mr-1" /> Reject
              </Button>
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}

function CredRow({ label, value, field, mono, onCopy, copied }) {
  return (
    <div className="flex items-center gap-2 p-2 bg-[#F8FAFC] rounded-md border border-[#E2E8F0]">
      <div className="text-xs text-[#64748B] w-32 shrink-0">{label}</div>
      <code className={`flex-1 text-sm ${mono ? 'font-mono' : ''} text-[#0A2342]`}>{value}</code>
      <Button size="sm" variant="ghost" onClick={() => onCopy(field, value)} data-testid={`copy-${field}`}>
        {copied ? <Check className="w-4 h-4 text-[#10B981]" /> : <Copy className="w-4 h-4" />}
      </Button>
    </div>
  );
}
